import React from "react";
import { Drawer } from "@mantine/core";

/** Desktop drawer width (design handoff "Shell (desktop)"). */
export const CALENDAR_DRAWER_WIDTH = 400;
/** Open / close width transition (the grid reflows over the same time). */
export const CALENDAR_DRAWER_TRANSITION_MS = 200;

/**
 * The custom property CalendarView sets from CALENDAR_DRAWER_WIDTH, so CSS
 * (grid push, FAB shift, header) and the drawer read one value.
 */
export const calendarDrawerWidthVar = {
  "--calendar-drawer-width": `${CALENDAR_DRAWER_WIDTH}px`,
} as React.CSSProperties;

interface CalendarDrawerProps {
  opened: boolean;
  /** Close button. Callers route it through their own gates (discard). */
  onClose: () => void;
  title?: React.ReactNode;
  /** False: no Mantine header at all (the drawer renders its own). */
  withHeader?: boolean;
  /** Accessible name when there is no title. */
  ariaLabel?: string;
  children: React.ReactNode;
}

/**
 * The shell every calendar drawer renders in: a right-side, non-modal 400px
 * Drawer beside a fully interactive calendar (CalendarView pushes the grid by
 * the same width while it is open). No scrim, no focus trap or scroll lock,
 * and it never closes on its own: outside clicks and Escape belong to the
 * caller. The content is a positioned flex column, so a NestedScreen inside
 * it covers the drawer and a sticky footer pins to the bottom.
 */
export default function CalendarDrawer({
  opened,
  onClose,
  title,
  withHeader = true,
  ariaLabel,
  children,
}: CalendarDrawerProps) {
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={CALENDAR_DRAWER_WIDTH}
      padding="lg"
      title={withHeader ? title : undefined}
      withCloseButton={withHeader}
      aria-label={ariaLabel}
      withOverlay={false}
      trapFocus={false}
      lockScroll={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      transitionProps={{
        duration: CALENDAR_DRAWER_TRANSITION_MS,
        timingFunction: "ease",
      }}
      styles={{
        // Flex column so the body fills the remaining height regardless of
        // header height (Mantine's content is display:block by default);
        // positioned so nested screens can cover it.
        content: {
          display: "flex",
          flexDirection: "column",
          position: "relative",
        },
        // No header divider or gap: the first section sits under the title.
        header: withHeader
          ? { borderBottom: "none", paddingBottom: 0, marginBottom: 0 }
          : { display: "none" },
        // Flex-fill body so a footer pins to the bottom.
        body: {
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          paddingTop: 4,
        },
      }}
    >
      {children}
    </Drawer>
  );
}
